import { clsx } from 'clsx'
import {
  Activity,
  AlertTriangle,
  Bell,
  Bug,
  ClipboardList,
  CreditCard,
  FileText,
  Globe,
  HardDrive,
  LayoutDashboard,
  Mail,
  Monitor,
  MonitorCheck,
  Network,
  Server,
  Settings,
  Shield,
  ShieldAlert,
  Users,
} from 'lucide-react'
import { NavLink } from 'react-router'
import { useAuth } from '@/lib/auth-context'

const ICON_MAP = {
  LayoutDashboard,
  Users,
  Mail,
  Server,
  HardDrive,
  Globe,
  AlertTriangle,
  ShieldAlert,
  CreditCard,
  Bell,
  FileText,
  Shield,
  Settings,
  Activity,
  ClipboardList,
  Monitor,
  MonitorCheck,
  Network,
  Bug,
} as const

interface NavItem {
  path: string
  label: string
  icon: keyof typeof ICON_MAP
  superAdminOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { path: '/', label: 'Overview', icon: 'LayoutDashboard' },
  { path: '/accounts', label: 'Accounts', icon: 'Users' },
  { path: '/emails', label: 'Email Explorer', icon: 'Mail' },
  { path: '/engine', label: 'Engine', icon: 'Server' },
  { path: '/infrastructure', label: 'Infrastructure', icon: 'Monitor' },
  { path: '/boxes', label: 'SMTP Boxes', icon: 'HardDrive' },
  { path: '/ips', label: 'Dedicated IPs', icon: 'Network' },
  { path: '/domains', label: 'Domains', icon: 'Globe' },
  { path: '/deliverability', label: 'Deliverability', icon: 'Activity' },
  { path: '/bounces', label: 'Bounces', icon: 'AlertTriangle' },
  { path: '/abuse', label: 'Abuse Monitor', icon: 'ShieldAlert' },
  { path: '/builder-applications', label: 'Builder Apps', icon: 'ClipboardList' },
  { path: '/staging', label: 'Staging Health', icon: 'MonitorCheck' },
  { path: '/status', label: 'Status', icon: 'Activity' },
  { path: '/billing', label: 'Billing', icon: 'CreditCard' },
  { path: '/alerts', label: 'Alerts', icon: 'Bell' },
  { path: '/audit', label: 'Audit Log', icon: 'FileText' },
]

const BOTTOM_NAV_ITEMS: NavItem[] = [
  { path: '/admin/users', label: 'Admin Users', icon: 'Shield', superAdminOnly: true },
  { path: '/bug-reports', label: 'Bug Reports', icon: 'Bug' },
  { path: '/settings', label: 'Settings', icon: 'Settings' },
]

interface SidebarProps {
  collapsed?: boolean
}

export function Sidebar({ collapsed = false }: SidebarProps): React.JSX.Element {
  const { isSuperAdmin } = useAuth()

  return (
    <aside
      className={clsx(
        'flex h-full flex-col border-r border-surface-3 bg-surface-0 transition-all duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center border-b border-surface-3 px-4">
        <NavLink to="/" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-admin-500">
            <Shield className="h-4 w-4 text-white" />
          </div>
          {!collapsed && (
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-semibold text-text-primary">Admin</span>
              <span className="rounded bg-admin-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-admin-700">
                internal
              </span>
            </div>
          )}
        </NavLink>
      </div>

      {/* Main navigation */}
      <nav className="flex-1 space-y-1 p-3">
        {NAV_ITEMS.map((item) => (
          <SidebarNavItem key={item.path} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* Divider + bottom nav */}
      <div className="border-t border-surface-3 p-3 space-y-1">
        {BOTTOM_NAV_ITEMS.map((item) => {
          if (item.superAdminOnly && !isSuperAdmin) return null
          return <SidebarNavItem key={item.path} item={item} collapsed={collapsed} />
        })}
      </div>
    </aside>
  )
}

function SidebarNavItem({
  item,
  collapsed,
}: {
  item: NavItem
  collapsed: boolean
}): React.JSX.Element {
  const Icon = ICON_MAP[item.icon]
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-admin-50 text-admin-700'
            : 'text-text-secondary hover:bg-surface-1 hover:text-text-primary',
          collapsed && 'justify-center',
        )
      }
    >
      <Icon className="h-5 w-5 shrink-0" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  )
}
